import React, { useRef, useEffect, useState } from 'react';

// Fully uncontrolled textarea with internal state management
const AutoExpandTextarea = ({ value, onChange, placeholder, disabled = false }) => {
  // Use internal state to track content - completely independent of parent re-renders
  const [internalValue, setInternalValue] = useState(value || '');
  const textareaRef = useRef(null);

  // Adjust height function
  const adjustHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  };

  // Initial setup - happens only once
  useEffect(() => {
    // Set initial value
    if (textareaRef.current) {
      textareaRef.current.value = value || '';
    }
    
    // Initial height adjustment
    adjustHeight();
    
    // Add input event listener for auto-expand
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.addEventListener('input', adjustHeight);
    }
    
    // Cleanup
    return () => {
      if (textarea) {
        textarea.removeEventListener('input', adjustHeight);
      }
    };
  }, []); // Empty dependency array - run once on mount

  // Simple change handler that doesn't trigger parent re-renders until blur
  const handleChange = (e) => {
    // Update internal state
    setInternalValue(e.target.value);
    // Adjust height
    adjustHeight();
  };
  
  // Only notify parent when focus is lost - prevents re-renders during typing
  const handleBlur = () => {
    if (onChange && textareaRef.current) {
      onChange({
        target: { value: textareaRef.current.value }
      });
    }
  };

  return (
    <textarea
      ref={textareaRef}
      defaultValue={value || ''} // Set initial value with defaultValue
      onChange={handleChange}
      onBlur={handleBlur} // Only update parent state when focus is lost
      placeholder={placeholder}
      disabled={disabled}
      style={{
        resize: 'none',
        overflow: 'hidden',
        minHeight: '100px',
        width: '100%',
        border: 'none',
        outline: 'none',
        fontFamily: 'Arial, sans-serif',
        fontSize: '16px',
        padding: '12px 16px',
        borderRadius: '16px',
        backgroundColor: disabled ? '#f5f5f5' : '#ffffff',
        lineHeight: '1.5',
        cursor: disabled ? 'not-allowed' : 'text', // Show not-allowed cursor when disabled
        zIndex: 5, // Ensure it's above any potential overlay
        opacity: disabled ? 0.6 : 1, // Grey out when disabled
      }}
    />
  );
};

export default function MessengerScrollablePreview({ 
  totalPages, 
  pageContents, 
  onContentChange, 
  step = 1,
  isEditable = true, // Default to editable
  designVersionShownRef = null // Ref to track design version state
}) {
  // Log incoming content for debugging
  console.log('MessengerScrollablePreview received content:', {
    contentLength: pageContents?.length || 0,
    hasContent: pageContents?.some(c => c) || false,
    sampleContent: pageContents?.[0]?.substring?.(0, 30) || 'empty'
  });
  
  // Simplified component - just displays the pageContents it receives
  // All content decision logic is now handled in PreviewSection

  // Simplified content change handler without additional logging
  const handleContentChange = (pageIndex, newContent) => {
    // Make sure page index is valid and silently pass through to parent
    if (pageIndex >= 0 && pageIndex < totalPages && onContentChange) {
      onContentChange(pageIndex, newContent);
    }
  };

  // Get filtered pages for the current step
  const getFilteredPages = () => {
    const filtered = [];
    
    // DEFENSIVE: Ensure pageContents is always treated as an array
    console.log('MESSENGER CONTENT CHECK:', { 
      type: typeof pageContents, 
      isArray: Array.isArray(pageContents),
      length: pageContents ? (Array.isArray(pageContents) ? pageContents.length : 'not array') : 'undefined'
    });
    
    // Create a safe version of the contents
    const actualContents = Array.isArray(pageContents) ? pageContents : Array(totalPages).fill('');

    if (step === 1) {
      // For step 1, show all pages but mark even pages as image pages if they have no text
      console.log('MessengerScrollablePreview: Step 1 mode - showing all pages');
      for (let i = 1; i <= totalPages; i++) {
        const pageContent = actualContents[i - 1];
        
        // Check if content is structured (array of elements) or string
        const elements = Array.isArray(pageContent) ? pageContent : (pageContent ? [{ content: pageContent }] : []);
        
        // Separate text and image elements
        // Only include text elements that have actual content
        const textElements = elements.filter(el => {
          if (el.type === 'image') return false;
          // Include if no type specified (legacy) and has content, or if type is 'text' and has content
          const hasContent = el.content && (typeof el.content === 'string' ? el.content.trim().length > 0 : true);
          return hasContent;
        });
        const imageElements = elements.filter(el => el.type === 'image');
        
        // Even pages are placeholders only if they have no text elements AND no image elements
        const isEvenPage = i % 2 === 0;
        const hasTextElements = textElements.length > 0;
        const hasImageElements = imageElements.length > 0;
        const isPlaceholder = isEvenPage && !hasTextElements && !hasImageElements;
        
        filtered.push({
          pageNum: i,
          pageIndex: i - 1,
          elements: textElements,
          imageElements: imageElements,
          isPlaceholder: isPlaceholder
        });
      }
    } else {
      // For other steps, show all pages
      console.log('MessengerScrollablePreview: Showing all pages - step:', step);
      for (let i = 1; i <= totalPages; i++) {
        const pageContent = actualContents[i - 1];
        // Check if content is structured (array of elements) or string
        const elements = Array.isArray(pageContent) ? pageContent : (pageContent ? [{ content: pageContent }] : []);
        
        // Separate text and image elements
        // Only include text elements that have actual content
        const textElements = elements.filter(el => {
          if (el.type === 'image') return false;
          // Include if no type specified (legacy) and has content, or if type is 'text' and has content
          const hasContent = el.content && (typeof el.content === 'string' ? el.content.trim().length > 0 : true);
          return hasContent;
        });
        const imageElements = elements.filter(el => el.type === 'image');
        
        filtered.push({
          pageNum: i,
          pageIndex: i - 1,
          elements: textElements,
          imageElements: imageElements,
          isPlaceholder: false
        });
      }
    }

    console.log('Filtered pages:', filtered);
    return filtered;
  };

  const filteredPages = getFilteredPages();

  return (
    <div style={{ 
      display: 'flex',
      flexDirection: 'column',
      gap: '24px'
    }}>
      {filteredPages.map((page) => (
        <div 
          key={`page-${page.pageNum}`}
          style={{
            marginBottom: '24px'
          }}
        >
          {/* Page header with page number */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '8px'
          }}>
            <div style={{
              fontSize: '14px',
              color: '#65676b',
              fontWeight: '500'
            }}>
              Page {page.pageNum}
            </div>
          </div>

          {page.isPlaceholder && (!page.elements || page.elements.length === 0) && (!page.imageElements || page.imageElements.length === 0) ? (
            /* Placeholder for image pages (even-numbered) - only show if completely empty */
            <div style={{
              backgroundColor: '#67696d', // Indigo-600 from tailwind (site primary color)
              borderRadius: '18px',
              padding: '16px',
              color: '#ffffff',
              textAlign: 'center',
              fontWeight: '500',
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
              marginBottom: '8px'
            }}>
              This page will be reserved for images
            </div>
          ) : (
            /* Multiple text element containers + image indicators */
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {page.elements && page.elements.length > 0 ? (
                page.elements.map((element, idx) => {
                  return (
                  <div
                    key={`element-${page.pageNum}-${idx}`}
                    style={{
                      backgroundColor: '#ffffff',
                      borderRadius: '18px',
                      border: '1px solid #e4e6eb',
                      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                      overflow: 'visible'
                    }}
                  >
                    <AutoExpandTextarea
                      value={element.content || ''}
                      onChange={(e) => {
                        // Preserve element structure by updating content while keeping ID
                        const updatedElements = (page.elements || []).map(el => 
                          el.id === element.id 
                            ? { ...el, content: e.target.value }
                            : el
                        );
                        handleContentChange(page.pageIndex, updatedElements);
                      }}
                      placeholder="Enter your story content..."
                      disabled={designVersionShownRef?.current || false}
                    />
                  </div>
                  );
                })
              ) : (!page.imageElements || page.imageElements.length === 0) ? (
                /* Empty state when no text elements AND no image elements */
                <div style={{
                  backgroundColor: '#ffffff',
                  borderRadius: '18px',
                  border: '1px solid #e4e6eb',
                  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                  overflow: 'visible'
                }}>
                  <AutoExpandTextarea
                    value=""
                    onChange={(e) => {
                      handleContentChange(page.pageIndex, e.target.value);
                    }}
                    placeholder="Enter your story content..."
                    disabled={designVersionShownRef?.current || false}
                  />
                </div>
              ) : null}
              
              {/* Image element indicators */}
              {page.imageElements && page.imageElements.length > 0 && (
                <div style={{
                  backgroundColor: '#f0f4ff',
                  borderRadius: '18px',
                  border: '2px solid #4f46e5',
                  padding: '12px',
                  marginTop: '8px'
                }}>
                  <div style={{
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#4f46e5',
                    marginBottom: '8px'
                  }}>
                    📸 {page.imageElements.length} Image{page.imageElements.length !== 1 ? 's' : ''} on this page
                  </div>
                  <div style={{
                    display: 'flex',
                    gap: '8px',
                    flexWrap: 'wrap'
                  }}>
                    {page.imageElements.map((img, idx) => (
                      <div
                        key={`image-${page.pageNum}-${idx}`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '60px',
                          height: '60px',
                          backgroundColor: '#e0e7ff',
                          borderRadius: '8px',
                          fontSize: '24px',
                          border: '1px solid #c7d2fe'
                        }}
                        title={img.imagePrompt || 'Generated image'}
                      >
                        {img.src ? '🖼️' : '⏳'}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
